import { isAndroid, isIOS, isNotNil, isWeb } from 'shared/src/utils';
import { useGoodsBasic } from './useGoodsBasic';
import { useEffect, useRef } from 'react';
import { useInstanceId, useOpenDeeplink } from 'shared/src/hooks';
import { useRootStackNavigation } from 'shared/src/hooks/useRootNavigation';
import { resolveWebviewWrappedRoute } from 'shared/src/webRoutePaths';
import { useGoodsSno } from '../hooks/useGoodsSno';

export const useCheckGoodsRedirectDeeplink = () => {
  const { data: goodsBasic, isSuccess } = useGoodsBasic();
  const { openDeeplink } = useOpenDeeplink();
  const instanceId = useInstanceId();
  const navigation = useRootStackNavigation();
  const redirect = useRef(false);
  const goodsSno = useGoodsSno();

  useEffect(() => {
    if (redirect.current) return;
    if ((isIOS || isAndroid) && isSuccess && isNotNil(goodsBasic)) {
      if (isNotNil(goodsBasic.redirect_deeplink)) {
        const state = navigation.getState();
        redirect.current = true;

        const redirectUrl = goodsBasic.redirect_deeplink.replace(
          'ably://webview?url=',
          '',
        );
        // 전용 화면이 있는 목적지(플레이스 상품 상세 등)면 그 화면으로. 범용 WebViewScreen
        // 으로 가면 주소가 `?url=...` 로 남아 목적지 경로가 유지되지 않는다.
        const wrappedRoute = resolveWebviewWrappedRoute(redirectUrl);

        navigation.reset({
          ...state,
          routes: state.routes.map(route => {
            if (
              route.name === 'GoodsDetail' &&
              route.params.goodsId === goodsSno
            ) {
              // 리다이렉트이므로 현재 상품 상세 route 를 목적지로 **교체**한다
              // (뒤로가기 시 리다이렉트가 다시 걸리는 화면으로 돌아가지 않게).
              return wrappedRoute !== null
                ? {
                    ...route,
                    name: wrappedRoute.screen,
                    params: wrappedRoute.params,
                  }
                : {
                    ...route,
                    name: 'WebViewScreen',
                    params: { url: redirectUrl },
                  };
            }
            return route;
          }),
        });

        return;
      }
    }

    if (isWeb && isNotNil(goodsBasic)) {
      if (isNotNil(goodsBasic.redirect_weblink)) {
        // 웹도 전용 화면이 있으면 SPA 안에서 교체 이동한다. location.href 는 전체 리로드라
        // 앱 상태(스택/쿼리 캐시)가 통째로 날아간다.
        const wrappedRoute = resolveWebviewWrappedRoute(
          goodsBasic.redirect_weblink,
        );
        if (wrappedRoute !== null) {
          redirect.current = true;
          const replace = navigation.replace as (
            screen: string,
            params: Record<string, unknown>,
          ) => void;
          replace(wrappedRoute.screen, wrappedRoute.params);
          return;
        }

        location.href = goodsBasic.redirect_weblink;
      }
    }
  }, [goodsBasic, goodsSno, instanceId, isSuccess, navigation, openDeeplink]);
};
